/**
 * Utility class for converting between iOS SF Symbols and Google Material Design icons.
 * This class provides bidirectional mapping between the two icon systems.
 */
export class IconConverter {
  static SF_TO_MATERIAL = new Map();
  static MATERIAL_TO_SF = new Map();

  static initializeMaps() {
    // Food and drink related icons
    this.SF_TO_MATERIAL.set("fork.knife", "restaurant");
    this.SF_TO_MATERIAL.set("cup.and.saucer", "coffee");
    this.SF_TO_MATERIAL.set("wineglass", "wine_bar");
    this.SF_TO_MATERIAL.set("takeoutbag.and.cup.and.straw", "fastfood");
    this.SF_TO_MATERIAL.set("cart", "storefront");
    this.SF_TO_MATERIAL.set("list.bullet", "menu_book");
    
    // Additional food-related icons
    this.SF_TO_MATERIAL.set("takeoutbag", "takeout_dining");
    this.SF_TO_MATERIAL.set("cup.and.saucer.fill", "local_cafe");
    this.SF_TO_MATERIAL.set("wineglass.fill", "liquor");
    this.SF_TO_MATERIAL.set("fork.knife.circle", "dining");
    this.SF_TO_MATERIAL.set("fork.knife.circle.fill", "dining");
    this.SF_TO_MATERIAL.set("takeoutbag.fill", "takeout_dining");
    this.SF_TO_MATERIAL.set("cup.and.saucer.circle", "coffee");
    this.SF_TO_MATERIAL.set("cup.and.saucer.circle.fill", "coffee");
    this.SF_TO_MATERIAL.set("wineglass.circle", "wine_bar");
    this.SF_TO_MATERIAL.set("wineglass.circle.fill", "wine_bar");
    this.SF_TO_MATERIAL.set("cart.fill", "storefront");
    this.SF_TO_MATERIAL.set("cart.circle", "storefront");
    this.SF_TO_MATERIAL.set("cart.circle.fill", "storefront");

    // Additional food and drink icons
    this.SF_TO_MATERIAL.set("flame", "heat");
    this.SF_TO_MATERIAL.set("moon", "nightlight");
    this.SF_TO_MATERIAL.set("sun.max", "sunny");
    this.SF_TO_MATERIAL.set("sunrise", "wb_twilight");
    this.SF_TO_MATERIAL.set("person.crop.circle", "chef_hat");
    this.SF_TO_MATERIAL.set("takeoutbag.and.cup.and.straw.fill", "bento");
    this.SF_TO_MATERIAL.set("fork.knife.circle.fill", "kebab_dining");
    this.SF_TO_MATERIAL.set("bowl", "rice_bowl");
    this.SF_TO_MATERIAL.set("fork.knife.circle.fill", "tapas");
    this.SF_TO_MATERIAL.set("sunrise.fill", "breakfast_dining");
    this.SF_TO_MATERIAL.set("table", "table_restaurant");
    this.SF_TO_MATERIAL.set("sunset.fill", "dinner_dining");
    this.SF_TO_MATERIAL.set("sunset", "dinner_dining");
    this.SF_TO_MATERIAL.set("fork.knife.circle.fill", "local_dining");
    this.SF_TO_MATERIAL.set("bowl.fill", "ramen_dining");
    this.SF_TO_MATERIAL.set("wineglass.fill", "local_drink");
    this.SF_TO_MATERIAL.set("birthday.cake", "bakery_dining");
    this.SF_TO_MATERIAL.set("circle.grid.cross", "local_pizza");
    this.SF_TO_MATERIAL.set("bell.badge", "room_service");
    this.SF_TO_MATERIAL.set("wineglass.fill", "liquor");
    this.SF_TO_MATERIAL.set("wineglass.fill", "local_bar");
    this.SF_TO_MATERIAL.set("cup.and.saucer.fill", "local_cafe");
    this.SF_TO_MATERIAL.set("sun.max.fill", "lunch_dining");

    // Create reverse mapping
    for (const [sfSymbol, materialIcon] of this.SF_TO_MATERIAL.entries()) {
      this.MATERIAL_TO_SF.set(materialIcon, sfSymbol);
    }
  }

  /**
   * Converts an iOS SF Symbol name to its Google Material Design icon equivalent.
   * @param {string} sfSymbol The iOS SF Symbol name
   * @returns {string} The corresponding Google Material Design icon name, or restaurant if no mapping exists
   */
  static sfToMaterial(sfSymbol) {
    const materialIcon = this.SF_TO_MATERIAL.get(sfSymbol);
    return materialIcon || "restaurant";
  }

  /**
   * Converts a Google Material Design icon name to its iOS SF Symbol equivalent.
   * @param {string} materialIcon The Google Material Design icon name
   * @returns {string} The corresponding iOS SF Symbol name, or fork.knife.circle.fill if no mapping exists
   */
  static materialToSf(materialIcon) {
    const sfIcon = this.MATERIAL_TO_SF.get(materialIcon);
    return sfIcon || "fork.knife.circle.fill";
  }

  /**
   * Gets a list of all supported Material Design icon names in alphabetical order.
   * @returns {string[]} Sorted list of all Material Design icon names that have mappings
   */
  static getAllMaterialDesignIconNames() {
    const uniqueIcons = new Set(this.SF_TO_MATERIAL.values());
    return Array.from(uniqueIcons).sort();
  }

  /**
   * Generates the Google Fonts stylesheet link for the given Material icon.
   * @param {string} materialIcon - The Material Design icon name
   * @returns {string} The stylesheet link for the icon
   */
  static getMaterialIconStylesheet(materialIcon) {
    return `https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&icon_names=${materialIcon}`;
  }
}

// Initialize the maps when the module is loaded
IconConverter.initializeMaps(); 